import DropdownSelect from "#components/DropdownSelect/index.js";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Chip, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import DataTable from "examples/Tables/DataTable";
import { formatDateTimeFromISOStringUTC } from "layouts/audits/components/utils";
import calculateDateRange from "layouts/logsheets/components/calculateDateRange";
import { useEffect, useMemo, useState } from "react";
import sheetTemplateCategoriesService from "services/sheet-template-categories-service";
import sheetService from "services/sheets-service";
import useOpen from "utils/useOpen";
import SheetsDialog from "./SheetsDialog";

export default function AllSheets({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();
  const [options, setOptions] = useState({
    sheetTemplateCategories: [],
  });

  const [sheets, setSheets] = useState(null);
  const [lastFilter, setLastFilter] = useState(() => {
    try {
      const value = JSON.parse(
        localStorage.getItem("all-sheets-filters-custom"),
      );

      if (!value) throw new Error("default");

      if (value.dateTime == "manual") {
        return value;
      } else {
        const dates = calculateDateRange(value.dateTime);
        return { ...value, ...dates };
      }
    } catch {
      const dates = calculateDateRange("today");
      return {
        type: "all",
        dateTime: "today",
        ...dates,
      };
    }
  });
  const [refreshSignal, setRefreshSignal] = useState(null);

  async function handleAddSheet(newElement) {
    const [response, data] = await sheetService.createSheet({
      ...newElement,
      options: newElement.options
        ? newElement.options.map((option) => {
            return {
              name: option.label,
              sequence: option.sequence,
              value: option.id,
            };
          })
        : null,
    });
    if (response.ok) {
      const [response, data] = await sheetService.getSheets(lastFilter);
      if (response.ok) {
        setSheets(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateSheet(newElement) {
    const [response, data] = await sheetService.updateSheet(newElement.id, {
      ...newElement,
      options: newElement.options
        ? newElement.options.map((option) => {
            return {
              name: option.label,
              sequence: option.sequence,
              value: option.id,
            };
          })
        : null,
    });
    if (response.ok) {
      const [response, data] = await sheetService.getSheets(lastFilter);
      if (response.ok) {
        setSheets(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteSheet(id) {
    const [response, data] = await sheetService.removeSheet(id);
    if (response.ok) {
      const [response, data] = await sheetService.getSheets(lastFilter);
      if (response.ok) {
        setSheets(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function fetch() {
    const [response, data] =
      await sheetTemplateCategoriesService.getTemplateCategories();
    if (response.ok) {
      setOptions({ ...options, sheetTemplateCategories: data });
    }
  }

  const rows = useMemo(() => {
    if (!sheets?.length)
      return sheets?.length === 0
        ? [
            {
              name: label("no-sheets-were-found"),
              disabled: true,
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return sheets.map((sheet) => {
      return {
        data: sheet,
        name: (
          <MDTypography variant="inherit">
            {sheet.statusId == 1 ? sheet?.sheetTemplate?.name : sheet?.name}
          </MDTypography>
        ),
        scheduledFor: (
          <MDTypography variant="inherit">
            {sheet.scheduledFor
              ? formatDateTimeFromISOStringUTC(sheet.scheduledFor)
              : label("none")}
          </MDTypography>
        ),
        status: (
          <Chip
            size="small"
            color={
              sheet.statusId == 1
                ? new Date() > new Date(sheet.scheduledFor)
                  ? "warning"
                  : "default"
                : sheet.statusId == 3
                  ? "success"
                  : "info"
            }
            sx={{
              color:
                sheet.statusId == 1
                  ? new Date() > new Date(sheet.scheduledFor)
                    ? "white.main"
                    : "black"
                  : sheet.statusId == 3
                    ? "white.main"
                    : "white.main",
            }}
            label={
              sheet.statusId == 1
                ? new Date() > new Date(sheet.scheduledFor)
                  ? "Scheduled - Late"
                  : "Scheduled - Early"
                : sheet.statusId == 3
                  ? "Completed"
                  : "In progress"
            }
          />
        ),

        actions:
          (!sheet.accountId && can("isAdmin")) || sheet.accountId ? (
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        sheet,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )} */}
              {permissions.create && sheet.statusId == 1 && (
                <Tooltip>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => {
                      open.change("create", {
                        sheet: {
                          ...sheet,
                          name: sheet.sheetTemplate?.name,
                          branchId: sheet.sheetTemplate?.branchId,
                        },
                      });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold", ml: -1 }}>add</Icon>
                    &nbsp;{"Fill logsheet"}
                  </MDButton>
                </Tooltip>
              )}
              {permissions.delete && sheet.statusId != 1 && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      open.change("alert", {
                        sheet,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [sheets, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: "Scheduled for",
        accessor: "scheduledFor",
      },
      {
        Header: "Status",
        accessor: "status",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        bodyCellStyle: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "end",
          alignItems: "end",
          width: "100%",
        },
      });
    }

    return columns;
  }, [permissions]);

  const dropdownOptions = useMemo(() => {
    return {
      sheetTemplateCategories: [
        { id: "all", label: "All" },
        ...options.sheetTemplateCategories.map((v) => ({
          id: v.id,
          label: v.name,
        })),
      ],
    };
  }, [options]);

  useEffect(() => {
    localStorage.setItem(
      "all-sheets-filters-custom",
      JSON.stringify(lastFilter),
    );
  }, [lastFilter]);

  useEffect(() => {
    fetch();
  }, []);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteSheet(open.get("alert").sheet.id);
          }}
        />
      ) : null}
      {open.get("create") ? (
        <SheetsDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddSheet}
          onClose={() => open.change("create", false)}
          data={open.get("create")?.sheet}
        />
      ) : null}
      {open.get("update") ? (
        <SheetsDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateSheet}
          onClose={() => open.change("update", false)}
          data={open.get("update")?.sheet}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("sheet-plural")}{" "}
                  {permissions.get ? `(${sheets?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-sheet-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Grid
                        container
                        spacing={1}
                        sx={{
                          mb: 1,
                        }}
                      >
                        <Grid item xs={1}>
                          <DropdownSelect
                            fullWidth
                            disableClearable
                            label="Type"
                            items={[
                              { id: "all", label: "All" },
                              { id: 1, label: "Measurable" },
                              { id: 2, label: "Actionable" },
                            ]}
                            initialValue={lastFilter.type}
                            onChange={(e) => {
                              const selectedId = e?.id;

                              setLastFilter((prev) => ({
                                ...prev,
                                type: selectedId,
                              }));
                              setRefreshSignal(Math.random());
                            }}
                          />
                        </Grid>{" "}
                        <Grid item xs={2}>
                          <DropdownSelect
                            fullWidth
                            disableClearable
                            label="Category"
                            items={dropdownOptions.sheetTemplateCategories}
                            initialValue={lastFilter.sheetTemplateCategoryId}
                            onChange={(e) => {
                              const selectedId = e?.id;

                              setLastFilter((prev) => ({
                                ...prev,
                                sheetTemplateCategoryId: selectedId,
                              }));
                              setRefreshSignal(Math.random());
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <DropdownSelect
                            fullWidth
                            disableClearable
                            label="Date and time"
                            items={[
                              { id: "yesterday", label: "Yesterday" },
                              { id: "today", label: "Today" },
                              { id: "tomorrow", label: "Tomorrow" },
                              { id: "current-week", label: "Current week" },
                              { id: "next-week", label: "Next week" },
                              { id: "current-month", label: "Current month" },
                              { id: "next-month", label: "Next month" },
                              {
                                id: "manual",
                                label: "Manual (max 1 month period)",
                              },
                            ]}
                            initialValue={lastFilter.dateTime}
                            onChange={(e) => {
                              const selectedId = e?.id;
                              const { from, to } =
                                calculateDateRange(selectedId);

                              setLastFilter((prev) => ({
                                ...prev,
                                dateTime: selectedId,
                                from:
                                  selectedId === "manual" ? prev.from : from,
                                to: selectedId === "manual" ? prev.to : to,
                              }));
                              setRefreshSignal(Math.random());
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <DateTimePicker
                            label="From"
                            disabled={lastFilter.dateTime !== "manual"}
                            value={
                              lastFilter.from ? dayjs(lastFilter.from) : null
                            }
                            slotProps={{ textField: { fullWidth: true } }}
                            onChange={(newDate) => {
                              setRefreshSignal(Math.random());
                              setLastFilter((prev) => {
                                const newFrom = newDate
                                  ? newDate.toDate()
                                  : null;
                                let newTo = prev.to;

                                if (newFrom && newTo) {
                                  const fromDay = dayjs(newFrom);
                                  const toDay = dayjs(newTo);

                                  // If 'From' is moved past 'To', OR the gap is larger than 1 month:
                                  // Auto-adjust 'To' to be exactly 1 month after the new 'From'
                                  if (
                                    fromDay.isAfter(toDay) ||
                                    toDay.diff(fromDay, "month", true) > 1
                                  ) {
                                    newTo = fromDay.add(1, "month").toDate();
                                  }
                                }

                                return { ...prev, from: newFrom, to: newTo };
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <DateTimePicker
                            label="To"
                            disabled={lastFilter.dateTime !== "manual"}
                            value={lastFilter.to ? dayjs(lastFilter.to) : null}
                            slotProps={{ textField: { fullWidth: true } }}
                            onChange={(newDate) => {
                              setRefreshSignal(Math.random());
                              setLastFilter((prev) => {
                                const newTo = newDate ? newDate.toDate() : null;
                                let newFrom = prev.from;

                                if (newTo && newFrom) {
                                  const toDay = dayjs(newTo);
                                  const fromDay = dayjs(newFrom);

                                  // If 'To' is moved before 'From', OR the gap is larger than 1 month:
                                  // Auto-adjust 'From' to be exactly 1 month before the new 'To'
                                  if (
                                    toDay.isBefore(fromDay) ||
                                    toDay.diff(fromDay, "month", true) > 1
                                  ) {
                                    newFrom = toDay
                                      .subtract(1, "month")
                                      .toDate();
                                  }
                                }

                                return { ...prev, to: newTo, from: newFrom };
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                    </LocalizationProvider>
                    <TableSearch
                      id="all-sheets"
                      refreshSignal={refreshSignal}
                      refreshSignalToQuery={(ref) => lastFilter}
                      internalLoading={!Boolean(sheets)}
                      fullWidth
                      setData={setSheets}
                      onSearch={async (request, callback) => {
                        setLastFilter((prev) => ({ ...prev, ...request }));

                        callback(
                          await sheetService.getSheets({
                            ...lastFilter,
                            ...request,
                          }),
                        );
                      }}
                      label={label("search-sheets-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(sheets)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      rowOptions={{
                        to: (row) => {
                          if (row?.statusId == 1) return "";
                          if (!row?.id) return "";
                          return "/sheets/" + row?.id;
                        },
                      }}
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
